import React, { useEffect, useState } from "react";
import { getIdentityColourIndex, getIdentitySeed } from "../../ui-kit/kuika-avatar/kuika-avatar";
import { avatarUrlOf, displayNameOf, loadPeopleDirectory, subscribePeople } from "./people-directory";

export interface BoardAvatarProps {
  // Stable user id. Name, initials, colour and photo are all resolved from the directory.
  userId: string | null | undefined;
  // The existing avatar class(es) for the slot (sizing/shape come from SCSS), e.g.
  // "kuika-board__rail-avatar kuika-board__rail-avatar--stack".
  className: string;
  title?: string;
  ariaHidden?: boolean;
  ariaLabel?: string;
}

function initials(name: string | null | undefined): string {
  const trimmed: string = (name ?? "").trim();
  if (!trimmed) return "";
  const parts: string[] = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// Renders a member's real profile photo when the workspace directory knows it, otherwise the
// initials circle. The name/photo are resolved from the user id via the directory, so identity is
// always the id and the display is always current (survives renames). Same className either way so
// the slot's existing size/shape styling applies; img adds object-fit via SCSS.
const BoardAvatar: React.FC<BoardAvatarProps> = ({ userId, className, title, ariaHidden, ariaLabel }) => {
  const [, bump] = useState<number>(0);
  // A photo URL can resolve but still fail to load (404, broken link, blank/transparent
  // image). Without this we'd render an empty coloured circle with no initials. Track the
  // load failure and fall back to the initials span just like a member with no photo.
  const [broken, setBroken] = useState<boolean>(false);

  useEffect(() => {
    const unsubscribe: () => void = subscribePeople(() => bump((value: number) => value + 1));
    void loadPeopleDirectory();
    return unsubscribe;
  }, []);

  const name: string = displayNameOf(userId);
  const photoUrl: string | undefined = avatarUrlOf(userId);

  // Reset the failure flag whenever the source changes, so a new (possibly valid) URL
  // gets a fresh chance to load instead of staying stuck on the initials fallback.
  useEffect(() => {
    setBroken(false);
  }, [photoUrl]);

  const resolvedTitle: string | undefined = title ?? (name || undefined);

  // The initials always render as the base layer of the circle; the photo (when we have
  // one) is overlaid on top. This is robust to every failure mode a profile-photo URL
  // can have: missing/empty (no overlay), 404/403 (onError removes the overlay), and —
  // crucially — a URL that loads "successfully" but is transparent or blank (the photo
  // simply doesn't cover the initials underneath). No URL-shape guessing required.
  const showPhoto: boolean = Boolean(photoUrl) && !broken;
  // The person's own colour, the same one `KuikaAvatar` gives them everywhere else. It is
  // applied here rather than by rendering that primitive because of the overlay above: the
  // board needs the initials to survive a photo that loads but is blank, which a component
  // that swaps one for the other cannot do. Inline so it beats the slot's own gradient;
  // without a user there is nobody to colour and the slot's empty style stands.
  const identitySeed: string | undefined = getIdentitySeed({ fullName: name });
  const identityGround: string | undefined = identitySeed
    ? `var(--avatar-${getIdentityColourIndex(identitySeed)})`
    : undefined;
  return (
    <span
      className={`${className} kuika-board__avatar`}
      style={identityGround ? { background: identityGround, color: "var(--white)" } : undefined}
      title={resolvedTitle}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
    >
      {initials(name)}
      {showPhoto ? (
        <img className="kuika-board__avatar-img" src={photoUrl} alt="" aria-hidden onError={() => setBroken(true)} />
      ) : null}
    </span>
  );
};

export default BoardAvatar;
